import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { useAuth } from '../../contexts/AuthContext';
import { promotionService } from '../../services/promotionService';
import { vetrineService } from '../../services/vetrineService';

const AdminPromotions = () => {
  const navigate = useNavigate();
  const { user, userRole } = useAuth();
  const [promotions, setPromotions] = useState([]);
  const [stores, setStores] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [formData, setFormData] = useState({
    storeId: '',
    titolo: '',
    descrizione: '',
    discountType: 'percentage',
    discountValue: '',
    termsConditions: '',
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    status: 'draft'
  });

  const discountTypeOptions = [
    { value: 'percentage', label: 'Percentuale (%)' },
    { value: 'fixed_amount', label: 'Importo Fisso (€)' },
    { value: 'buy_x_get_y', label: 'Compra X Prendi Y' },
    { value: 'free_shipping', label: 'Spedizione Gratuita' },
    { value: 'other', label: 'Altro' }
  ];

  const statusOptions = [
    { value: 'draft', label: 'Bozza' },
    { value: 'active', label: 'Attiva' },
    { value: 'scheduled', label: 'Programmata' },
    { value: 'expired', label: 'Scaduta' },
    { value: 'archived', label: 'Archiviata' }
  ];

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (userRole !== 'admin') {
      navigate('/');
      return;
    }

    loadData();
  }, [user, userRole, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [promotionsData, storesData, statsData] = await Promise.all([
        promotionService?.getAllPromotions(),
        vetrineService?.getAllStores(),
        promotionService?.getPromotionStats()
      ]);
      setPromotions(promotionsData);
      setStores(storesData);
      setStats(statsData);
    } catch (err) {
      setError(err?.message || 'Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      storeId: '',
      titolo: '',
      descrizione: '',
      discountType: 'percentage',
      discountValue: '',
      termsConditions: '',
      startDate: format(new Date(), 'yyyy-MM-dd'),
      endDate: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
      status: 'draft'
    });
    setEditingPromotion(null);
    setShowForm(false);
  };

  const handleEdit = (promotion) => {
    setFormData({
      storeId: promotion?.storeId || '',
      titolo: promotion?.titolo || '',
      descrizione: promotion?.descrizione || '',
      discountType: promotion?.discountType || 'percentage',
      discountValue: promotion?.discountValue || '',
      termsConditions: promotion?.termsConditions || '',
      startDate: promotion?.startDate ? format(new Date(promotion?.startDate), 'yyyy-MM-dd') : '',
      endDate: promotion?.endDate ? format(new Date(promotion?.endDate), 'yyyy-MM-dd') : '',
      status: promotion?.status || 'draft'
    });
    setEditingPromotion(promotion);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccess('');

    if (!formData?.storeId || !formData?.titolo || !formData?.descrizione || !formData?.startDate || !formData?.endDate) {
      setError('Compila tutti i campi obbligatori');
      return;
    }

    if (new Date(formData?.endDate) <= new Date(formData?.startDate)) {
      setError('La data di fine deve essere successiva alla data di inizio');
      return;
    }

    try {
      if (editingPromotion) {
        await promotionService?.updatePromotion(editingPromotion?.id, formData);
        setSuccess('Promozione aggiornata con successo');
      } else {
        await promotionService?.createPromotion(formData);
        setSuccess('Promozione creata con successo');
      }
      resetForm();
      await loadData();
    } catch (err) {
      setError(err?.message || 'Errore nel salvataggio');
    }
  };

  const handleDelete = async (promotionId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa promozione?')) return;

    try {
      await promotionService?.deletePromotion(promotionId);
      setSuccess('Promozione eliminata con successo');
      await loadData();
    } catch (err) {
      setError(err?.message || 'Errore nell\'eliminazione');
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-success text-success-foreground';
      case 'scheduled':
        return 'bg-warning text-warning-foreground';
      case 'expired':
        return 'bg-muted text-muted-foreground';
      case 'draft':
        return 'bg-secondary text-secondary-foreground';
      case 'archived':
        return 'bg-destructive/20 text-destructive';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusLabel = (status) => {
    const option = statusOptions?.find(opt => opt?.value === status);
    return option?.label || status;
  };

  const getDiscountTypeLabel = (type) => {
    const option = discountTypeOptions?.find(opt => opt?.value === type);
    return option?.label || type;
  };

  const filteredPromotions = promotions?.filter(promo => {
    if (activeTab === 'all') return true;
    return promo?.status === activeTab;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header onFilterClick={() => {}} />
        <div className="pt-24 pb-20 px-6">
          <div className="max-w-7xl mx-auto flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Gestione Promozioni Admin - CLICCAMI.NET</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header onFilterClick={() => {}} />
        <div className="pt-24 pb-20 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-heading font-bold text-4xl text-foreground mb-2">
                  Gestione Promozioni
                </h1>
                <p className="text-muted-foreground">Crea, modifica e traccia tutte le promozioni con metriche di engagement</p>
              </div>
              <Button
                onClick={() => setShowForm(!showForm)}
                className="flex items-center gap-2"
              >
                <Icon name={showForm ? 'X' : 'Plus'} size={20} />
                {showForm ? 'Annulla' : 'Nuova Promozione'}
              </Button>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg text-destructive">
                {error}
              </div>
            )}

            {success && (
              <div className="mb-6 p-4 bg-success/10 border border-success rounded-lg text-success">
                {success}
              </div>
            )}

            {stats && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-lg shadow-sm p-6 border border-border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-muted-foreground text-sm">Totale Promozioni</span>
                    <Icon name="Tag" size={20} className="text-primary" />
                  </div>
                  <p className="text-3xl font-bold text-foreground">{stats?.total}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {stats?.active} attive, {stats?.scheduled} programmate
                  </p>
                </div>

                <div className="bg-white rounded-lg shadow-sm p-6 border border-border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-muted-foreground text-sm">Visualizzazioni</span>
                    <Icon name="Eye" size={20} className="text-primary" />
                  </div>
                  <p className="text-3xl font-bold text-foreground">{stats?.totalViews?.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {stats?.totalClicks} click totali
                  </p>
                </div>

                <div className="bg-white rounded-lg shadow-sm p-6 border border-border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-muted-foreground text-sm">Engagement Rate</span>
                    <Icon name="TrendingUp" size={20} className="text-success" />
                  </div>
                  <p className="text-3xl font-bold text-foreground">{stats?.avgEngagementRate}%</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Media su tutte le promozioni
                  </p>
                </div>

                <div className="bg-white rounded-lg shadow-sm p-6 border border-border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-muted-foreground text-sm">Conversion Rate</span>
                    <Icon name="Target" size={20} className="text-warning" />
                  </div>
                  <p className="text-3xl font-bold text-foreground">{stats?.avgConversionRate}%</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {stats?.totalConversions} conversioni totali
                  </p>
                </div>
              </div>
            )}

            {showForm && (
              <div className="bg-white rounded-lg shadow-sm p-6 mb-8 border border-border">
                <h2 className="text-2xl font-heading font-bold text-foreground mb-6">
                  {editingPromotion ? 'Modifica Promozione' : 'Nuova Promozione'}
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Select
                      label="Negozio"
                      required
                      value={formData?.storeId}
                      onChange={(value) => handleInputChange('storeId', value)}
                      options={stores?.map(store => ({
                        value: store?.id,
                        label: store?.nomeNegozio
                      }))}
                      placeholder="Seleziona negozio"
                    />

                    <Input
                      label="Titolo Promozione"
                      required
                      value={formData?.titolo}
                      onChange={(e) => handleInputChange('titolo', e?.target?.value)}
                      placeholder="Es: Sconto 20% su tutti i prodotti"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Descrizione <span className="text-destructive">*</span>
                    </label>
                    <textarea
                      required
                      value={formData?.descrizione}
                      onChange={(e) => handleInputChange('descrizione', e?.target?.value)}
                      placeholder="Descrizione dettagliata della promozione"
                      rows={4}
                      className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Select
                      label="Tipo Sconto"
                      value={formData?.discountType}
                      onChange={(value) => handleInputChange('discountType', value)}
                      options={discountTypeOptions}
                    />

                    {(formData?.discountType === 'percentage' || formData?.discountType === 'fixed_amount') && (
                      <Input
                        label="Valore Sconto"
                        type="number"
                        step="0.01"
                        value={formData?.discountValue}
                        onChange={(e) => handleInputChange('discountValue', e?.target?.value)}
                        placeholder={formData?.discountType === 'percentage' ? '20' : '10.00'}
                      />
                    )}
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Termini e Condizioni
                    </label>
                    <textarea
                      value={formData?.termsConditions}
                      onChange={(e) => handleInputChange('termsConditions', e?.target?.value)}
                      placeholder="Termini e condizioni della promozione"
                      rows={3}
                      className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Input
                      label="Data Inizio"
                      type="date"
                      required
                      value={formData?.startDate}
                      onChange={(e) => handleInputChange('startDate', e?.target?.value)}
                    />

                    <Input
                      label="Data Fine"
                      type="date"
                      required
                      value={formData?.endDate}
                      onChange={(e) => handleInputChange('endDate', e?.target?.value)}
                    />

                    <Select
                      label="Stato"
                      value={formData?.status}
                      onChange={(value) => handleInputChange('status', value)}
                      options={statusOptions}
                    />
                  </div>

                  <div className="flex gap-4">
                    <Button type="submit" className="flex items-center gap-2">
                      <Icon name="Save" size={18} />
                      {editingPromotion ? 'Aggiorna Promozione' : 'Crea Promozione'}
                    </Button>
                    <Button type="button" variant="outline" onClick={resetForm}>
                      Annulla
                    </Button>
                  </div>
                </form>
              </div>
            )}

            <div className="bg-white rounded-lg shadow-sm border border-border">
              <div className="border-b border-border">
                <div className="flex gap-4 px-6 pt-4">
                  {[
                    { value: 'all', label: 'Tutte', count: promotions?.length },
                    { value: 'active', label: 'Attive', count: stats?.active },
                    { value: 'scheduled', label: 'Programmate', count: stats?.scheduled },
                    { value: 'expired', label: 'Scadute', count: stats?.expired }
                  ]?.map(tab => (
                    <button
                      key={tab?.value}
                      onClick={() => setActiveTab(tab?.value)}
                      className={`pb-4 px-2 font-medium text-sm border-b-2 transition-colors ${
                        activeTab === tab?.value
                          ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {tab?.label} ({tab?.count || 0})
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-6">
                {filteredPromotions?.length === 0 ? (
                  <div className="text-center py-12">
                    <Icon name="Tag" size={48} className="mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">Nessuna promozione trovata</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Promozione</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Negozio</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Periodo</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Stato</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Metriche</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPromotions?.map(promo => (
                          <tr key={promo?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                            <td className="py-4 px-4">
                              <div>
                                <p className="font-medium text-foreground">{promo?.titolo}</p>
                                <p className="text-sm text-muted-foreground line-clamp-1">{promo?.descrizione}</p>
                                {promo?.discountValue && (
                                  <p className="text-xs text-primary mt-1">
                                    {getDiscountTypeLabel(promo?.discountType)}: {promo?.discountValue}{promo?.discountType === 'percentage' ? '%' : '€'}
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="py-4 px-4">
                              <p className="text-sm text-foreground">{promo?.store?.nomeNegozio}</p>
                              <p className="text-xs text-muted-foreground">{promo?.store?.categoria}</p>
                            </td>
                            <td className="py-4 px-4">
                              <p className="text-sm text-foreground">
                                {promo?.startDate && format(new Date(promo?.startDate), 'dd MMM yyyy', { locale: it })}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {promo?.endDate && format(new Date(promo?.endDate), 'dd MMM yyyy', { locale: it })}
                              </p>
                            </td>
                            <td className="py-4 px-4">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(promo?.status)}`}>
                                {getStatusLabel(promo?.status)}
                              </span>
                            </td>
                            <td className="py-4 px-4">
                              <div className="flex flex-col gap-1 text-xs">
                                <div className="flex items-center gap-2">
                                  <Icon name="Eye" size={14} className="text-muted-foreground" />
                                  <span>{promo?.viewCount} views</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Icon name="MousePointerClick" size={14} className="text-muted-foreground" />
                                  <span>{promo?.clickCount} click</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Icon name="Share2" size={14} className="text-muted-foreground" />
                                  <span>{promo?.shareCount} share</span>
                                </div>
                              </div>
                            </td>
                            <td className="py-4 px-4">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEdit(promo)}
                                  className="flex items-center gap-1"
                                >
                                  <Icon name="Edit" size={16} />
                                  Modifica
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(promo?.id)}
                                  className="flex items-center gap-1 text-destructive hover:text-destructive"
                                >
                                  <Icon name="Trash2" size={16} />
                                  Elimina
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminPromotions;